import { useHistory } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { AdminGeneralInfo } from './useGetAdminByIdState';
import {
  UPDATE_ADMIN_USER,
  UPDATE_USER_MUTATION,
  UpdateSuperAdminInput,
  UpdateSuperAdminResponse,
} from '../../../apollo/queries/adminUsers/updateAdmin.queries';
import { ROUTE_ADMIN } from '../../../components/Routing/routeHelpers';
import { AdminToastHelper } from '../../../components/AdminUsers/AdminToast';
import { getTenantInput } from '../../AddAdminUser/hooks/useAddAdminUserAPI';
import { PutUser } from '../../../apollo/queries/adminUsers/addAdmin.queries';
import { createDynamicApolloClient } from '../../../apollo/getApolloClient';
import { AdminRole } from '../../AddAdminUser/hooks/useAddAdminUser';

type ReturnType = [
  updateAdmin: (adminId: string, adminDetails: AdminGeneralInfo) => void,
];

const useUpdateAdmin = (adminDetails: AdminGeneralInfo): ReturnType => {
  const history = useHistory();
  const client = createDynamicApolloClient(adminDetails.tenant);
  const [updateUser] = useMutation<UpdateSuperAdminResponse>(
    UPDATE_ADMIN_USER,
    {
      onCompleted: (responseData) => {
        const data = responseData && responseData.updateAdminUser;
        if (data) {
          history.push(ROUTE_ADMIN.adminsPage);
          AdminToastHelper.adminUpdatedSuccessfully();
        }
      },
      onError: (e) => {
        AdminToastHelper.error();
      },
    },
  );

  const [updateUserByTenant] = useMutation<UpdateSuperAdminResponse>(
    UPDATE_USER_MUTATION,
    {
      onCompleted: (responseData) => {
        const data = responseData?.updateUser;
        if (data) {
          history.push(ROUTE_ADMIN.adminsPage);
          AdminToastHelper.adminUpdatedSuccessfully();
        }
      },
      client: client,
      onError: (e) => {
        AdminToastHelper.error();
      },
    },
  );

  const updateAdmin = (adminId: string, adminDetails: AdminGeneralInfo) => {
    const {
      firstName,
      lastName,
      password,
      role,
      tenant,
      organisations,
      email,
    } = adminDetails;

    const admin: UpdateSuperAdminInput = {
      id: adminId,
      firstName,
      email,
      lastName,
      roles: [role],
      ...getTenantInput(role, tenant, organisations),
    };

    const updateAdminByTenant: PutUser = {
      uuid: adminId,
      firstName,
      lastName,
      email,
      roles: [role],
      enabled: true,
      onboarded: true,
      username: email,
      ...(password?.length > 0 && { password: password }),
      ...getTenantInput(role, tenant, organisations),
    };

    if (password.length > 0) {
      admin.password = password;
    }

    if (
      role === AdminRole.ORGANIZATION_ADMIN ||
      role === AdminRole.ALEF_ORG_SUPPORT
    ) {
      updateUserByTenant({ variables: { input: updateAdminByTenant } });
    } else {
      updateUser({ variables: { input: admin } });
    }
  };

  return [updateAdmin];
};

export default useUpdateAdmin;
